import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { invoicesApi, clientsApi, formatCurrency, formatInvoiceDate } from '../lib/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  Plus, 
  FileText, 
  Eye, 
  Trash2,
  Search
} from 'lucide-react';
import { Input } from '../components/ui/input';

const InvoicesPage = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [invoicesRes, clientsRes] = await Promise.all([
        invoicesApi.getAll(),
        clientsApi.getAll()
      ]);
      setInvoices(invoicesRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;
    try {
      await invoicesApi.delete(id);
      toast.success('Invoice deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete invoice');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-zinc-100 text-zinc-600',
      sent: 'bg-blue-100 text-blue-700',
      paid: 'bg-green-100 text-green-700',
    };
    return styles[status] || styles.draft;
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client?.name || 'Unknown Client';
  };

  const getClientRegion = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client?.region;
  };

  const filteredInvoices = invoices.filter(inv => 
    inv.invoice_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    getClientName(inv.client_id).toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-zinc-900">Invoices</h1>
          <p className="text-zinc-500 mt-1">Manage all your invoices</p>
        </div>
        <Button 
          onClick={() => navigate('/invoices/create')}
          className="bg-orange-500 hover:bg-orange-600"
          data-testid="create-invoice-page-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Invoice
        </Button>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-zinc-400" />
        <Input
          placeholder="Search invoices..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 max-w-md"
          data-testid="invoice-search-input"
        />
      </div>

      {/* Invoices List */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-20 bg-zinc-100 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : filteredInvoices.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-zinc-200">
          <FileText className="w-16 h-16 text-zinc-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-zinc-700">
            {searchTerm ? 'No matching invoices' : 'No invoices yet'}
          </h3>
          <p className="text-zinc-500 mt-1 mb-4">
            {searchTerm ? 'Try a different search term' : 'Create your first invoice to get started'}
          </p>
          {!searchTerm && (
            <Button onClick={() => navigate('/invoices/create')} data-testid="create-first-invoice-page-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Invoice
            </Button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-100">
                <th className="text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider py-4 px-6">
                  Invoice
                </th>
                <th className="text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider py-4 px-6">
                  Client
                </th>
                <th className="text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider py-4 px-6">
                  Date
                </th>
                <th className="text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider py-4 px-6">
                  Status
                </th>
                <th className="text-right text-xs font-semibold text-zinc-500 uppercase tracking-wider py-4 px-6">
                  Amount
                </th>
                <th className="text-right text-xs font-semibold text-zinc-500 uppercase tracking-wider py-4 px-6">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredInvoices.map((invoice) => (
                <tr 
                  key={invoice.id} 
                  className="border-b border-zinc-50 hover:bg-zinc-50 transition-colors"
                  data-testid={`invoice-row-${invoice.id}`}
                >
                  <td className="py-4 px-6">
                    <p className="font-mono font-medium text-zinc-900">{invoice.invoice_number}</p>
                  </td>
                  <td className="py-4 px-6">
                    <p className="text-zinc-700">{getClientName(invoice.client_id)}</p>
                  </td>
                  <td className="py-4 px-6">
                    <p className="text-zinc-600 font-mono text-sm">{formatInvoiceDate(invoice.invoice_date, getClientRegion(invoice.client_id))}</p>
                  </td>
                  <td className="py-4 px-6">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium capitalize ${getStatusBadge(invoice.status)}`}>
                      {invoice.status}
                    </span>
                  </td>
                  <td className="py-4 px-6 text-right">
                    <p className="font-mono font-semibold text-zinc-900">
                      {formatCurrency(invoice.total, invoice.currency)}
                    </p>
                  </td>
                  <td className="py-4 px-6">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/invoices/${invoice.id}`)}
                        data-testid={`view-invoice-${invoice.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(invoice.id)}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        data-testid={`delete-invoice-${invoice.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default InvoicesPage;
